/**
 * 작성자 : 한영광
 * 날짜 : 2025.06.02
 * 경로 : 출역 관리 - 방문자 현황
 */

import { useEffect, useState, useRef, RefObject } from 'react';
import { toast } from 'react-toastify';
import { PulseLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { IoChevronUpSharp } from 'react-icons/io5';
import { useRecoilState } from 'recoil';
import { useOutletContext, useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { ERROR, LIST_COUNT } from '../../../_constants';
import { IUser, userState } from '../../../atoms';
import { IComCdList } from 'customTypes';
import { BtnGhost } from '../../../components/Button';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import { FloatingButtonToTop } from '../../../assets/styles/FloatingButtonToTop';
import { LoadingModalBackground } from '../../../assets/styles/Modal';
import { useSetAuth } from '../../../utils/useSetAuth';
import { trimObject } from '../../../utils/trimObject';
import { todayYYYYMMDD } from '../../../utils/formatDate';
import { scrollToNodeTop } from '../../../utils/scrollToNodeTop';
import { setComCdListState } from '../../../utils/setComCdListState';
import { useDetectScrolledToBottom } from '../../../utils/useDetectScrolledToBottom';
import useOnKeydownF9 from '../../../utils/useOnKeydownF9';
import Input from '../../../components/Input';
import Portal from '../../../components/Portal';
import TuiGrid from '../../../components/Table/TuiGrid';
import SelectBox from '../../../components/SelectBox';
import TuiGridWrapper from '../../../components/Table/TuiGridWrapper';
import RangePicker, { onChangeRangeInput } from '../../../components/RangePicker';
import { logPost } from '../../../services/log';
import { useGetFrozenCount } from '../../../utils/getFrozenCount';
import { ContentsContainerRoot } from '../../../assets/styles/ContentsContainerRoot';
import i18n from '../../../translation/i18n';
import useSetListCount from '../../../utils/useSetListCount';
import { apiGet } from '../../../services/_common';
import ShortcutButton from '../../../components/button/ShortcutButton';
import useTuiGridSize from '../../../hooks/useTuiGridSize';
import SearchFilterModal from '../../../components/Modal/SearchFilterModal';
import { showExcelButton } from '../../../utils/deviceAuth';
import ImageBtnRenderer from '../../../components/ImageBtnRenderer';
import Thumbnail from '../../../components/Modal/Thumbnail';
import * as XLSX from 'xlsx';

const Root = styled(ContentsContainerRoot)`
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 0.5rem;
  padding: 2rem 0;

  > :nth-child(1) {
    align-self: flex-start;
  }

  .badge {
    font-weight: 500;
    font-size: 0.875rem;
    padding: 0.125rem 0.5rem;
    border-radius: 0.25rem;
    margin: 0 0.5rem;
    display: inline-block;
    min-width: 1.75rem;
    text-align: center;
  }

  .blue {
    color: var(--primary);
    background-color: var(--tonal-blue);
  }

  .red {
    color: var(--filled-red);
    background-color: var(--tonal-red);
  }

  .green {
    color: var(--filled-green);
    background-color: var(--tonal-green);
  }
`;

const Visit = ({ showPhoto, showCarnum, visitHnumYn, visitBdateYn }: { showPhoto: boolean; showCarnum: boolean; visitHnumYn: boolean; visitBdateYn: boolean }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const { isF9Pressed, setIsF9Pressed } = useOnKeydownF9(); // f9키 프레스 훅
  const { listCount, setListCount, patchUserMenuAPI } = useSetListCount(); // 검색줄수설정 훅
  const location = useLocation();
  const size = useOutletContext<any>();
  const toDay = todayYYYYMMDD();
  const [userInfo, setUserInfo] = useRecoilState<IUser>(userState);
  const { eExcelYn, eListYn } = userInfo;
  const [tableState, setTableState] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const vNameRef = useRef<HTMLInputElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const [visibleRangePicker, setVisibleRangePicker] = useState(false);
  const [rangeState, setRangeState] = useState([
    {
      startDate: location.state?.startDate ? new Date(location.state?.startDate) : new Date(),
      endDate: location.state?.endDate ? new Date(location.state?.endDate) : new Date(),
      key: 'selection',
    },
  ]);
  const [listCountComCdList, setListCountComCdList] = useState<IComCdList[]>([]); // 검색줄수 공통코드
  const [searchOption, setSearchOption] = useState({
    vName: location.state?.vName || '',
    start: location.state?.startDate || toDay,
    end: location.state?.endDate || toDay,
  });
  const [excelBtn, setExcelBtn] = useState(false);
  const { isBottom } = useDetectScrolledToBottom(tableRef);
  const [openModal, setOpenModal] = useState<any>({ status: false, type: '', code: { wCd: '' }, index: 0 });
  const [rowKey, setRowKey] = useState('');
  const [filterTableState, setFilterTableState] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);
  const frozenCount = useGetFrozenCount();
  const showExcelBtn = showExcelButton(auth.excelAuth);

  useEffect(() => {
    setLoading(true);
    getVisitAPI().then((res: any) => {
      if (res.status === 200) {
        setComCdListState(LIST_COUNT, setListCountComCdList, false);
        logPost({
          hCd: userInfo.hCd,
          sCd: userInfo.sCd,
          userId: userInfo.userId,
          menu: '방문자 현황',
          action: '조회',
          etc: ``,
        });
        setLoading(false);
      }
    });
  }, [userInfo.sCd]);

  useEffect(() => {
    const baseColumns = [
      {
        header: t('방문 구분'),
        name: 'visitType',
        align: 'center',
        sortable: true,
        width: 100,
      },
      {
        header: t('방문자명'),
        name: 'vName',
        align: 'left',
        isPrivate: true,
        maskingType: 'name',
        sortable: true,
        width: 120,
        renderer: { classNames: ['text_secondary'] },
      },
      {
        header: t('방문부서'),
        name: 'dept',
        align: 'center',
        sortable: true,
        minWidth: 120,
      },
      {
        header: t('방문목적'),
        name: 'purpose',
        align: 'center',
        sortable: true,
        minWidth: 120,
      },
    ];

    const newColumns = [];

    // showPhoto가 true일 때만 사진 컬럼 추가
    if (showPhoto) {
      newColumns.push({
        header: t('사진'),
        name: 'vUrl',
        align: 'center',
        width: 80,
        renderer: {
          type: ImageBtnRenderer,
          options: {
            onImgButtonClicked: onVisitorImgButtonClicked,
          },
        },
      });
    }

    // 기본 컬럼들 추가
    newColumns.push(...baseColumns);

    if (visitBdateYn) {
      newColumns.push({
        header: t('생년월일'),
        name: 'vBdate',
        isPrivate: true,
        maskingType: 'birth',
        align: 'center',
        sortable: true,
        width: 140,
      });
    }

    if (visitHnumYn) {
      newColumns.push({
        header: t('연락처'),
        name: 'vTel',
        isPrivate: true,
        align: 'center',
        maskingType: 'tel',
        sortable: true,
        minWidth: 120,
      });
    }

    // showCarnum이 true일 때만 차량번호 컬럼 추가
    if (showCarnum) {
      newColumns.push({
        header: t('차량번호'),
        name: 'carNum',
        align: 'center',
        sortable: true,
        width: 140,
      });
    }

    // 방문일자 컬럼 추가
    newColumns.push({
      header: t('방문일자'),
      name: 'logDate',
      align: 'center',
      sortable: true,
      minWidth: 120,
    });

    newColumns.push({
      header: t('교육 일자 (잔여 일 수)'),
      name: 'eduDate',
      align: 'center',
      sortable: true,
      minWidth: 180,
    });

    // 서명 이미지 컬럼 추가
    newColumns.push({
      header: t('서명'),
      name: 'eduSign',
      align: 'center',
      width: 80,
      renderer: {
        type: ImageBtnRenderer,
        options: {
          onImgButtonClicked,
          showDefaultImgWhenEmpty: false,
        },
      },
    });

    setColumns(newColumns);
  }, [tableState, i18n.language, showPhoto, showCarnum, visitHnumYn, visitBdateYn]);

  useEffect(() => {
    if (isF9Pressed) {
      onClickSearch();
      setIsF9Pressed(false);
    }
  }, [isF9Pressed]);

  useEffect(() => {
    if (!rangeState[0].startDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, start: dayjs(rangeState[0].startDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].startDate]);

  useEffect(() => {
    if (!rangeState[0].endDate) {
      return;
    }
    setSearchOption((prev: any) => ({ ...prev, end: dayjs(rangeState[0].endDate).format('YYYY-MM-DD') }));
  }, [rangeState[0].endDate]);

  // 공통 계산 로직
  const calculateEducationInfo = (el: any) => {
    const visitDate = dayjs(el.logDate).startOf('day');
    const edueExpire = dayjs(el.eduExpiredDate).startOf('day');

    if (!edueExpire.isValid()) {
      console.error('잘못된 날짜 형식:', el.eduExpiredDate);
      return null;
    }

    const daysDiff = edueExpire.diff(visitDate, 'day') - 1;
    const displayText = daysDiff > 0 ? `만료 ${daysDiff}일 전` : daysDiff < 0 ? `만료 ${Math.abs(daysDiff)}일 경과` : '만료 당일';

    return { daysDiff, displayText };
  };

  const getVisitTypeInfo = (gubun: string) => {
    const isCar = gubun === 'CAR';
    return {
      text: isCar ? '차량' : '일반',
      color: isCar ? 'green' : 'blue',
    };
  };

  const transformDataForExcel = (data: any[]) => {
    return data
      .map((el: any) => {
        const eduInfo = calculateEducationInfo(el);
        if (!eduInfo) return null;

        const visitTypeInfo = getVisitTypeInfo(el.gubun);

        return {
          ...el,
          visitType: visitTypeInfo.text,
          eduDate: `${el.eduDate ? dayjs(el.eduDate).format('YYYY-MM-DD') : ''} (${eduInfo.displayText})`,
          eduSign: el.eduSign ? 'O' : '',
        };
      })
      .filter(Boolean); // null 값 제거
  };

  const transformDataForTable = (data: any[]) => {
    return data
      .map((el: any) => {
        const eduInfo = calculateEducationInfo(el);
        if (!eduInfo) return null;

        const visitTypeInfo = getVisitTypeInfo(el.gubun);

        const eduDateWithLeftday = el.eduDate ? `<span class='badge ${eduInfo.daysDiff <= 7 ? 'red' : ''}'>${dayjs(el.eduDate).format('YYYY-MM-DD')} (${eduInfo.displayText})</span>` : '';

        const gubunFormat = `<span class='badge ${visitTypeInfo.color}'>${visitTypeInfo.text}</span>`;

        return {
          ...el,
          eduDate: eduDateWithLeftday,
          visitType: gubunFormat,
        };
      })
      .filter(Boolean); // null 값 제거
  };

  // 방문자 현황 조회 API
  const getVisitAPI = async () => {
    const newSearchOption = {
      ...searchOption,
      aDate1: searchOption.start.replaceAll('-', ''),
      aDate2: searchOption.end.replaceAll('-', ''),
    };
    const { start, end, ...finalSearchObj } = newSearchOption; // searchOption에서 start, end값 삭제
    const trimData = trimObject(finalSearchObj);
    const req = { ...trimData, hCd: userInfo.hCd, sCd: userInfo.sCd };
    const res = await apiGet({ path: '/visit', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      const tableData = transformDataForTable(data);
      setTableState(tableData);
    } else {
      toast.error(t(ERROR));
    }
    setLoading(false);
    return res;
  };

  // 테이블 안에 이미지 버튼 클릭
  const onImgButtonClicked = (index: number) => {
    const { eduSign = '' } = tableState[index]; // 서명 이미지
    // const { vUrl = '' } = tableState[index]; // 사진 이미지
    setOpenModal({
      status: true,
      type: 'imgView',
      data: { aImgPath: eduSign, style: { width: '15vw', height: '10vw' } },
      thumbnailOnly: true,
    });
  };

  // 테이블 안에 이미지 버튼 클릭
  const onVisitorImgButtonClicked = (index: number) => {
    const { vUrl = '' } = tableState[index]; // 사진 이미지
    setOpenModal({
      status: true,
      type: 'imgView',
      data: { aImgPath: vUrl, style: { width: '25vw', height: '20vw' } },
      thumbnailOnly: true,
    });
  };

  // 검색 버튼 클릭
  const onClickSearch = (filterRef?: RefObject<HTMLDivElement>) => {
    setRowKey('');
    if (!openModal.status || (openModal.status && openModal.type === 'filter')) {
      if (!searchOption.end && !searchOption.start) {
        toast.warning(t('출역일자를 입력하세요'));
      } else if (searchOption.end && searchOption.start > searchOption.end) {
        toast.warning(t('출역일자를 다시 입력하세요'));
        setSearchOption((prev: any) => ({ ...prev, end: '' }));
      } else {
        setLoading(true);
        if (filterRef) {
          // 모바일일 경우 검색 필터 모달 창 닫기
          filterRef.current?.classList.remove('active');
          setOpenModal({ status: false, type: 'filter' });
        }
        setVisibleRangePicker(false);
        navigate({
          pathname: location.pathname,
          search: '',
        });
        getVisitAPI();
      }
    }
  };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    if (location.state?.sjName) {
      location.state.sjName = '';
    }
    Object.keys(searchOption).map((el: any) => {
      return setSearchOption((prev) => ({ ...prev, [el]: '', start: toDay, end: toDay }));
    });
    setRangeState([{ startDate: new Date(), endDate: new Date(), key: 'selection' }]);
  };

  // 엑셀 저장 버튼 클릭
  const onClickExcelSave = () => {
    // SheetJS로 엑셀 생성 (열 너비 지정 포함)
    const filterTableStateWithoutHtml = filterTableState.map((el: any) => {
      return {
        ...el,
        vTel: el.originvTel,
        vName: el.originvName,
        vBdate: el.originvBdate,
        visitType: el.visitType.replace(/<[^>]*>?/g, ''),
        eduDate: el.eduDate.replace(/<[^>]*>?/g, ''),
        eduSign: el.eduSign?.trim() ? 'O' : '',
      };
    });

    const rows =
      filterTableStateWithoutHtml && filterTableStateWithoutHtml.length > 0
        ? filterTableStateWithoutHtml
        : transformDataForExcel(filterTableStateWithoutHtml.length > 0 ? filterTableStateWithoutHtml : tableState);

    if (!rows || rows.length === 0) return;

    // vUrl 컬럼이 있으면 해당컬럼 삭제
    // Grid 컬럼 순서/헤더를 그대로 사용 (hidden=false 또는 excelOnly=true 포함)
    const exportColumns = columns.filter((c: any) => !c.hidden || c.excelOnly);
    const vUrlIndex = exportColumns.findIndex((c: any) => c.name === 'vUrl');
    if (vUrlIndex !== -1) {
      exportColumns.splice(vUrlIndex, 1);
    }
    const headerRow = ['No.', ...exportColumns.map((c: any) => c.header)];
    const dataRows = rows.map((r: any, idx: number) => [idx + 1, ...exportColumns.map((c: any) => r[c.name] ?? '')]);

    // AOA → 시트
    const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);

    // 각 컬럼별 최대 문자열 길이로 너비 계산 (한글/영문 혼합 고려 약식 가중치)
    const calcTextWidth = (text: any) => {
      const s = String(text ?? '');
      const korean = (s.match(/[\u3131-\uD79D]/g) || []).length;
      const ascii = s.length - korean;
      return korean * 2 + ascii;
    };

    const colCount = headerRow.length;
    const colWidths = Array.from({ length: colCount }, (_, colIdx: number) => {
      const headerWidth = calcTextWidth(headerRow[colIdx]);
      const maxDataWidth = dataRows.reduce((max: number, row: any[]) => Math.max(max, calcTextWidth(row[colIdx])), 0);
      const width = Math.max(headerWidth, maxDataWidth) + 4; // 여백
      return { wch: Math.min(Math.max(width, 8), 60) };
    });

    (worksheet as any)['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '방문자 현황');

    XLSX.writeFile(workbook, 'PMS_방문자현황.xlsx');

    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '방문자 현황',
      action: '엑셀 저장',
      etc: ``,
    });
  };

  // 방문자 Row 클릭
  const onClickRow = (rowData: any, columnName: any, filteredIndex: number) => {
    setRowKey(`${filteredIndex}`);
  };

  const renderExcelBtn = () => {
    if (showExcelBtn) {
      return (
        <BtnGhost onClick={onClickExcelSave}>
          <span className='icon-ms-xlsx'>X</span> {t('엑셀')}
        </BtnGhost>
      );
    }
    return null;
  };

  const onClickRangeInput = () => {
    if (!visibleRangePicker) setVisibleRangePicker(true);
  };

  // 근로자명 입력창에서 엔터입력시 검색
  const loginOnEnterKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (vNameRef.current) onClickSearch();
    }
  };

  // 필터 버튼 클릭
  const onClickFilter = () => {
    setOpenModal({ status: true, type: 'filter' });
  };

  return (
    <div className='contents'>
      <div className='content-container oneColumn'>
        <Root loading={loading}>
          <SearchOptions>
            {size.innerSize.W > 1024 ? (
              <>
                {isBottom && (
                  <FloatingButtonToTop>
                    {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                    <button type='button' onClick={() => scrollToNodeTop(tableRef)}>
                      <IoChevronUpSharp size={20} style={{ stroke: 'white' }} />
                    </button>
                  </FloatingButtonToTop>
                )}
                <div className='inputsWrapper'>
                  <div className='inputForm-row-fit'>
                    <span className='calendarLabel'>{t('방문일자')}</span>
                    <div className='inputForm-col'>
                      <div className='flex-basic'>
                        <input id='startInput' type='text' value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                        <span className='inputDash'> ~ </span>
                        <input id='endInput' type='text' value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                      </div>
                      {visibleRangePicker && (
                        <div className='rangePickerWrapper' style={{ left: '20%' }}>
                          <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                        </div>
                      )}
                    </div>
                    <button type='button' className='date-picker-btn' onClick={onClickRangeInput}>
                      <span className='material-symbols-rounded'>calendar_month</span>
                    </button>
                  </div>
                  <div className='inputForm-row'>
                    <div className='inputForm-col'>
                      <Input
                        placeholder={t('방문자명')}
                        label=''
                        type='text'
                        id='vName'
                        name='vName'
                        state={searchOption}
                        setState={setSearchOption}
                        inputRef={vNameRef}
                        onKeyDown={loginOnEnterKeyDown}
                        className='textInput'
                      />
                    </div>
                  </div>
                </div>
                <div className='inputsWrapper'>
                  <div className='secondSearchOption'>
                    <div className='flex-basic textBtnGroup'>
                      <ShortcutButton icon='search' buttonText='검색' shortcut='F9' onClick={onClickSearch} />
                      <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
                      <div className='searchResult'>
                        {t('총')}
                        <span>{tableState.length}</span>
                        {t('개')}
                      </div>
                      <div className='inputForm-row'>
                        <div className='inputForm-col withLabelComCf'>
                          <label htmlFor='useYn'>{t('보기 설정')}</label>
                          <SelectBox
                            options={listCountComCdList}
                            defaultOption={listCount[LIST_COUNT]}
                            state={listCount}
                            setState={setListCount}
                            stateKey={LIST_COUNT}
                            initiateKey={listCount[LIST_COUNT]}
                            setTableLinesAPI={patchUserMenuAPI}
                            optionHeight='height-sm'
                            rsearch
                          />
                        </div>
                      </div>
                    </div>
                    <div className='flex-basic iconBtnGroup'>{renderExcelBtn()}</div>
                  </div>
                </div>
              </>
            ) : (
              <div className='filter-wrapper'>
                <div className='inputsWrapper'>
                  <div className='inputForm-row' style={{ width: 'calc(100% - 3.5rem)' }}>
                    <div className='inputForm-col'>
                      <Input
                        placeholder={t('근로자명')}
                        label=''
                        type='text'
                        id='vName'
                        name='vName'
                        state={searchOption}
                        setState={setSearchOption}
                        inputRef={vNameRef}
                        onKeyDown={loginOnEnterKeyDown}
                        ico='search'
                      />
                    </div>
                  </div>
                  <BtnGhost onClick={onClickFilter} className='filter-btn'>
                    <span className='material-symbols-rounded'>filter_alt</span>
                  </BtnGhost>
                </div>
                <div className='inputsWrapper'>
                  <div className='flex-basic iconBtnGroup'>{renderExcelBtn()}</div>
                </div>
              </div>
            )}
          </SearchOptions>
          <TuiGridWrapper componentRef={componentRef}>
            <TuiGrid
              data={tableState}
              filterTableState={filterTableState}
              setFilterTableState={setFilterTableState}
              columns={columns}
              perPage={Number(listCount[LIST_COUNT])}
              usePagenation
              onClickRow={onClickRow}
              scrollX
              height={tuiHeight}
              eListYn={eListYn}
              eExcelYn={eExcelYn}
              rowKey={rowKey}
              frozenCount={frozenCount}
              setRowKey={setRowKey}
              excelBtn={excelBtn}
              setExcelBtn={setExcelBtn}
            />
          </TuiGridWrapper>
        </Root>
      </div>
      {loading && (
        <LoadingModalBackground>
          <PulseLoader className='flex-center' color='rgb(0, 122, 255)' size='1rem' />
        </LoadingModalBackground>
      )}
      <Portal openModal={openModal?.status}>{openModal.status && openModal.type === 'imgView' && <Thumbnail openModal={openModal} setOpenModal={setOpenModal} />}</Portal>

      {size.innerSize.W <= 1024 && (
        <Portal openModal>
          <SearchFilterModal openModal={openModal} setOpenModal={setOpenModal} onClickSearch={onClickSearch} onClickReset={onClickInitiateSearchOption}>
            <div className='filter-option-wrapper'>
              <div className='filter-option-col filter-underline filter-option-date'>
                <span className='calendarLabel'>{t('출역일자')}</span>
                <div className='inputForm-col'>
                  <div className='flex-basic' role='presentation' onClick={onClickRangeInput}>
                    <input id='startInput' type='text' readOnly value={searchOption.start} onChange={(e) => onChangeRangeInput(e, 'start', setRangeState, setSearchOption)} />
                    <span className='inputDash'> ~ </span>
                    <input id='endInput' type='text' readOnly value={searchOption.end} onChange={(e) => onChangeRangeInput(e, 'end', setRangeState, setSearchOption)} />
                  </div>
                  {visibleRangePicker && (
                    <div className='rangePickerWrapper'>
                      <RangePicker state={rangeState} setState={setRangeState} setVisible={setVisibleRangePicker} />
                    </div>
                  )}
                </div>
              </div>
              <div className='filter-option-cont'>
                <div className='filter-option-row'>
                  <label htmlFor='useYn'>{t('보기 설정')}</label>
                  <SelectBox
                    options={listCountComCdList}
                    defaultOption={listCount.cdName}
                    state={listCount}
                    setState={setListCount}
                    stateKey={LIST_COUNT}
                    initiateKey={listCount[LIST_COUNT]}
                    setTableLinesAPI={patchUserMenuAPI}
                    optionHeight='height-sm'
                    rsearch
                  />
                </div>
              </div>
            </div>
          </SearchFilterModal>
        </Portal>
      )}
    </div>
  );
};

export default Visit;
